import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'

export default function OptionForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState([])
  const [allParts, setAllParts] = useState([])
  const [name, setName] = useState('')
  const [optionParts, setOptionParts] = useState([])

  useEffect(() => {
    // Fetch all parts first
    fetch('/api/parts')
      .then(response => response.json())
      .then(data => {
        setAllParts(data)

        if (isEdit) {
          // Fetch option
          fetch(`/api/options/${id}`)
            .then(response => response.json())
            .then(optionData => {
              setName(optionData.name || '')
              setOptionParts(optionData.parts?.map(p => ({
                part_id: p.part_id,
                quantity: p.quantity
              })) || [])
              setLoading(false)
            })
            .catch(err => {
              console.error('Error fetching option:', err)
              setLoading(false)
            })
        } else {
          setLoading(false)
        }
      })
      .catch(err => {
        console.error('Error fetching parts:', err)
        setLoading(false)
      })
  }, [id, isEdit])

  const addPart = () => {
    setOptionParts([...optionParts, { part_id: '', quantity: 1 }])
  }

  const removePart = (index) => {
    setOptionParts(optionParts.filter((_, i) => i !== index))
  }

  const updatePart = (index, field, value) => {
    const updated = [...optionParts]
    updated[index][field] = field === 'quantity' ? parseInt(value) || 0 : value
    setOptionParts(updated)
  }

  const getPartById = (partId) => {
    return allParts.find(p => p.id === parseInt(partId))
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrors([])

    // Filter out empty parts and zero quantities
    const validParts = optionParts.filter(op => op.part_id && op.quantity !== 0)

    const url = isEdit ? `/api/options/${id}` : '/api/options'
    const method = isEdit ? 'PUT' : 'POST'

    fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        option: {
          name: name,
          parts: validParts
        }
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.errors) {
          setErrors(data.errors)
        } else {
          navigate('/options')
        }
      })
      .catch(err => {
        console.error('Error saving option:', err)
        setErrors(['An unexpected error occurred'])
      })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-6">
        <Link to="/options" className="text-gray-600 hover:text-gray-900">
          &larr; Back to Options
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEdit ? 'Edit Option' : 'New Option'}
        </h1>

        {errors.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <ul className="list-disc list-inside text-red-700">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Option Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Gold Package, No Fan Option"
              className="w-full md:w-1/2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* Info about negative quantities */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Use negative quantities to remove parts from a configuration.
              For example, if a configuration includes 1 fan and you want this option to remove it,
              set the fan quantity to -1.
            </p>
          </div>

          {/* Parts */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Part Modifications</h2>
              <button
                type="button"
                onClick={addPart}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                + Add Part
              </button>
            </div>

            {optionParts.length === 0 ? (
              <p className="text-gray-500 text-sm">No parts added yet. Click "Add Part" to begin.</p>
            ) : (
              <div className="space-y-3">
                {optionParts.map((op, index) => (
                  <div key={index} className="flex items-center space-x-4 bg-gray-50 p-3 rounded-md">
                    <div className="flex-1">
                      <select
                        value={op.part_id}
                        onChange={(e) => updatePart(index, 'part_id', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                      >
                        <option value="">Select a part...</option>
                        {allParts.map(part => (
                          <option key={part.id} value={part.id}>
                            {part.description} ({formatCurrency(part.price)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-24">
                      <input
                        type="number"
                        value={op.quantity}
                        onChange={(e) => updatePart(index, 'quantity', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                        placeholder="Qty"
                      />
                    </div>
                    <div className="w-20 text-center">
                      {op.quantity > 0 ? (
                        <span className="text-green-600 text-sm font-medium">+{op.quantity}</span>
                      ) : op.quantity < 0 ? (
                        <span className="text-red-600 text-sm font-medium">{op.quantity}</span>
                      ) : (
                        <span className="text-gray-400 text-sm">0</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removePart(index)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-4">
            <Link
              to="/options"
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800"
            >
              {isEdit ? 'Update Option' : 'Create Option'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
